import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  User as UserIcon, Loader2, Save, Camera, Copy, Check, Share2,
  Sparkles, X, Users, Mail as MailIcon, Crown, Award, Link as LinkIcon,
  Send, Building2, Phone, MapPin, ShieldCheck, ArrowRight, Zap, Trash2,
  Bell, KeyRound, FileText, MessageCircle, Code, ExternalLink, Radio, CalendarClock,
} from "lucide-react";
import { api } from "./api";
import { useAuth } from "./AuthContext";
import { toHandle } from "./utils/handle";

// Compress an image file → data URL, target ~50KB.
async function compressToDataUrl(file, maxSize = 256, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let { width, height } = img;
        const scale = Math.min(1, maxSize / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        // JPEG for smaller size; step down quality if still too big.
        let dataUrl = canvas.toDataURL("image/jpeg", quality);
        let q = quality;
        while (dataUrl.length > 90_000 && q > 0.35) {
          q -= 0.1;
          dataUrl = canvas.toDataURL("image/jpeg", q);
        }
        resolve(dataUrl);
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Profile({ open, onClose, onOpenApp }) {
  const { user, setUser } = useAuth();
  const isBoss = (user?.role || "").toLowerCase() === "boss";
  const isSuper = !!user?.is_super_admin;
  const canEditFully = isBoss || isSuper || (user?.role || "").toLowerCase() === "individual";
  const [tab, setTab] = useState("profile"); // profile | referral

  // Profile fields
  const [name, setName] = useState(user?.name || "");
  const [designation, setDesignation] = useState(user?.designation || "");
  const [companyName, setCompanyName] = useState(user?.company_name || "");
  const [companyAddress, setCompanyAddress] = useState(user?.company_address || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [currency, setCurrency] = useState(user?.currency || "INR");
  const [bio, setBio] = useState(user?.bio || "");
  const [photo, setPhoto] = useState(user?.photo || "");
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  // Referral
  const [ref, setRef] = useState(null);
  const [refBusy, setRefBusy] = useState(true);
  const [copied, setCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteMsg, setInviteMsg] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Refresh referral info when the profile panel opens
    (async () => {
      setRefBusy(true);
      try {
        const { data } = await api.get("/referrals/me");
        setRef(data);
      } catch (e) {
        console.error("referral fetch failed", e);
      } finally {
        setRefBusy(false);
      }
    })();
    // Sync fields from user prop when reopened
    setName(user?.name || "");
    setDesignation(user?.designation || "");
    setCompanyName(user?.company_name || "");
    setCompanyAddress(user?.company_address || "");
    setPhone(user?.phone || "");
    setCurrency(user?.currency || "INR");
    setBio(user?.bio || "");
    setPhoto(user?.photo || "");
    setSavedMsg(""); setErr("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pickPhoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setErr("Please choose an image file."); return; }
    try {
      const d = await compressToDataUrl(file);
      setPhoto(d);
    } catch (e) {
      setErr("Failed to read image: " + e.message);
    }
  };

  const saveProfile = async () => {
    setSaving(true); setErr(""); setSavedMsg("");
    try {
      const body = canEditFully
        ? { name, designation, company_name: companyName, company_address: companyAddress, phone, bio, photo, currency }
        : { name, photo }; // employees can only tweak name + photo (their company is inherited)
      const { data } = await api.patch("/users/me/profile", body);
      setUser(data.user);
      setSavedMsg("Profile saved.");
      setTimeout(() => setSavedMsg(""), 2500);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };


  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Fallback
      const t = document.createElement("textarea");
      t.value = text; document.body.appendChild(t); t.select();
      document.execCommand("copy"); document.body.removeChild(t);
      setCopied(true); setTimeout(() => setCopied(false), 1600);
    }
  };

  const shareNative = async () => {
    if (!ref?.share_link) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join me on HEXA OS",
          text: "HEXA OS — an AI-native web operating system. Sign up with my link:",
          url: ref.share_link,
        });
      } catch { /* user cancelled */ }
    } else {
      copy(ref.share_link);
    }
  };

  const sendInviteEmail = async () => {
    if (!inviteEmail.trim()) return;
    setInviteBusy(true); setInviteResult(null);
    try {
      const { data } = await api.post("/referrals/invite-email", { to_email: inviteEmail, message: inviteMsg });
      setInviteResult(data);
      if (data.sent) { setInviteEmail(""); setInviteMsg(""); }
    } catch (e) {
      setInviteResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setInviteBusy(false);
    }
  };

  if (!open) return null;

  const initial = (user?.name || user?.address || "?")[0].toUpperCase();

  // Portalled to document.body on purpose. Rendered inline it sits inside the OS
  // top bar, whose .cmdstrip backdrop-filter makes it the containing block for
  // fixed children — so this "full screen" overlay was resolving against a 28px
  // strip. That mispositioned the dialog over the app and broke its inner
  // scroll area, which is why the ZeptoMail settings could not be scrolled.
  return createPortal(
    <div
      data-testid="profile-modal"
      className="fixed inset-0 z-[500] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[min(880px,96vw)] rounded-xl bg-[#0c1224] border border-white/10 flex flex-col"
        style={{ boxShadow: "0 30px 100px rgba(0,0,0,0.75)", maxHeight: "90vh" }}
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-white/8 flex items-center gap-3">
          <UserIcon size={16} className="text-[#a78bfa]" />
          <div className="mono text-xs uppercase tracking-[0.28em] text-white/85">My Profile</div>
          <div className="mono text-[10px] text-white/40">// hexa id · {toHandle(user?.address)}</div>
          <button
            data-testid="profile-close"
            onClick={onClose}
            className="ml-auto text-white/55 hover:text-white p-1"
          >
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-4 border-b border-white/8 flex items-center gap-0">
          {[
            ["profile", "Profile", UserIcon],
            ["referral", "Referral", Award],
            ["notifications", "Notifications", Bell],
            ["integrations", "Integrations", Zap],
            ["reports", "Reports", CalendarClock],
            ...((canEditFully) ? [["api", "API", KeyRound]] : []),
            ...((isSuper) ? [["tracking", "Tracking", Radio]] : []),
          ].map(([key, label, Icon]) => (
            <button
              key={key}
              data-testid={`profile-tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-3 py-2 mono text-[10px] uppercase tracking-[0.28em] flex items-center gap-1.5 transition ${
                tab === key ? "text-[#a78bfa] border-b-2 border-[#a78bfa]" : "text-white/45 hover:text-white/70"
              }`}
            >
              <Icon size={11} /> {label}
            </button>
          ))}
        </div>

        {/* Body */}
        {/* No inline ref here. An inline arrow is a fresh function every render,
            so React re-ran it on every re-render and forced scrollTop back to 0 —
            typing in a settings field snapped the panel to the top, which read as
            a broken scrollbar. `key={tab}` already remounts this on tab change,
            so scroll resets between tabs on its own. */}
        <div className="flex-1 overflow-y-auto p-5" data-testid="profile-body-scroll" key={tab}>
          {tab === "profile" && (
            <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
              {/* Photo + identity */}
              <div className="flex lg:flex-col items-center lg:items-start gap-4">
                <div className="relative">
                  {photo ? (
                    <img
                      src={photo}
                      alt="Profile"
                      className="w-32 h-32 rounded-full object-cover border-2 border-[#a78bfa]/40"
                      data-testid="profile-photo-preview"
                    />
                  ) : (
                    <div
                      className="w-32 h-32 rounded-full grid place-items-center text-4xl font-semibold text-white"
                      style={{ background: "linear-gradient(135deg, #a78bfa, #3b82f6)" }}
                      data-testid="profile-photo-initial"
                    >
                      {initial}
                    </div>
                  )}
                  {canEditFully && (
                    <button
                      data-testid="profile-photo-pick"
                      onClick={() => fileRef.current?.click()}
                      className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-[#a78bfa] hover:bg-[#8b5cf6] grid place-items-center text-white shadow"
                      title="Upload profile picture"
                    >
                      <Camera size={13} />
                    </button>
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => pickPhoto(e.target.files?.[0])}
                  />
                </div>
                <div className="min-w-0 lg:mt-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {isSuper && <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#fbbf24]/15 text-[#fbbf24] border border-[#fbbf24]/40"><Crown size={9} className="inline mr-0.5" />super</span>}
                    {isBoss && !isSuper && <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#a78bfa]/15 text-[#a78bfa] border border-[#a78bfa]/40">boss</span>}
                    {!isBoss && !isSuper && <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/8 text-white/70">{user?.role || "user"}</span>}
                    {user?.external_identity && <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#60a5fa]/15 text-[#60a5fa] border border-[#60a5fa]/40">byoe</span>}
                  </div>
                  <div className="text-sm text-white/90 mt-2 font-medium">{name || user?.name}</div>
                  <div className="mono text-[10px] text-white/45 truncate">{user?.address}</div>
                </div>

                {/* Admin/Boss quick-access buttons */}
                {(isBoss || isSuper) && (
                  <div className="w-full space-y-1 mt-2 pt-3 border-t border-white/8">
                    <div className="mono text-[9px] uppercase tracking-[0.22em] text-white/40 mb-1">// admin actions</div>
                    <button
                      data-testid="profile-open-admin"
                      onClick={() => { onClose(); onOpenApp?.(isSuper ? "admin-ai" : "attendance"); }}
                      className="w-full text-left px-2.5 py-2 rounded flex items-center gap-2 text-[11px] hover:bg-white/5"
                      style={{ border: "1px solid rgba(167,139,250,0.35)", background: "rgba(167,139,250,0.06)", color: "#c7c1ff" }}
                    >
                      <Users size={11} /> {isSuper ? "Open Admin AI" : "Team & Employees"}
                      <ArrowRight size={11} className="ml-auto" />
                    </button>
                    <button
                      data-testid="profile-open-attendance"
                      onClick={() => { onClose(); onOpenApp?.("attendance"); }}
                      className="w-full text-left px-2.5 py-2 rounded flex items-center gap-2 text-[11px] hover:bg-white/5 text-white/70 border border-white/10"
                    >
                      <ShieldCheck size={11} /> Add / Delete User & Access
                      <ArrowRight size={11} className="ml-auto" />
                    </button>
                  </div>
                )}
              </div>

              {/* Fields */}
              <div className="space-y-3">
                {!canEditFully && (
                  <div className="p-2.5 rounded text-[11px] mono uppercase tracking-widest text-white/55 bg-white/3 border border-white/10">
                    // employee mode · you can edit only name & photo · company details inherited from your boss
                  </div>
                )}
                <Field label="Full name">
                  <input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} className="input" placeholder="Your name" />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Designation" disabled={!canEditFully}>
                    <input data-testid="profile-designation" value={designation} onChange={(e) => setDesignation(e.target.value)} disabled={!canEditFully} className="input disabled:opacity-40" placeholder="e.g. Product Manager" />
                  </Field>
                  <Field label="Phone">
                    <div className="flex items-center gap-2 input"><Phone size={11} className="text-white/40" />
                      <input data-testid="profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={!canEditFully} className="flex-1 bg-transparent outline-none disabled:opacity-40" placeholder="+91 98765 43210" />
                    </div>
                  </Field>
                  <Field label="Currency">
                    <select data-testid="profile-currency" value={currency} onChange={(e) => setCurrency(e.target.value)} disabled={!canEditFully} className="input disabled:opacity-40">
                      <option value="INR">₹ INR — Indian Rupee</option>
                      <option value="USD">$ USD — US Dollar</option>
                      <option value="EUR">€ EUR — Euro</option>
                      <option value="GBP">£ GBP — British Pound</option>
                      <option value="AED">AED — UAE Dirham</option>
                    </select>
                  </Field>
                </div>
                <Field label="Company name">
                  <div className="flex items-center gap-2 input"><Building2 size={11} className="text-white/40" />
                    <input data-testid="profile-company-name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} disabled={!canEditFully} className="flex-1 bg-transparent outline-none disabled:opacity-40" placeholder="e.g. Acme Inc." />
                  </div>
                </Field>
                <Field label="Company address">
                  <div className="flex items-start gap-2 input"><MapPin size={11} className="text-white/40 mt-1" />
                    <textarea data-testid="profile-company-address" value={companyAddress} onChange={(e) => setCompanyAddress(e.target.value)} disabled={!canEditFully} rows={2} className="flex-1 bg-transparent outline-none disabled:opacity-40 resize-y" placeholder="Street, City, State, Country" />
                  </div>
                </Field>
                {canEditFully && (
                  <Field label="Short bio">
                    <textarea data-testid="profile-bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={2} className="input resize-y" placeholder="Optional — a line about what you do" />
                  </Field>
                )}

                <div className="flex items-center gap-2 pt-2">
                  <button
                    data-testid="profile-save"
                    onClick={saveProfile}
                    disabled={saving || !name.trim()}
                    className="btn-accent text-xs flex items-center gap-1.5 px-4 py-2"
                    style={{ borderColor: "rgba(167,139,250,0.55)" }}
                  >
                    {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                    Save changes
                  </button>
                  {savedMsg && <span data-testid="profile-saved-msg" className="text-emerald-300 text-xs mono">{savedMsg}</span>}
                  {err && <span data-testid="profile-error" className="text-red-300 text-xs mono">{err}</span>}
                </div>
              </div>
            </div>
          )}


          {tab === "referral" && (
            <div className="space-y-5">
              {refBusy && !ref && (
                <div className="flex items-center gap-2 text-white/55 text-sm"><Loader2 size={14} className="animate-spin" /> Loading referral…</div>
              )}
              {ref && (
                <>
                  {/* Coins headline */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="p-4 rounded-lg border" style={{ background: "linear-gradient(135deg, rgba(251,191,36,0.15), rgba(251,191,36,0.05))", borderColor: "rgba(251,191,36,0.4)" }}>
                      <div className="mono text-[10px] uppercase tracking-widest text-[#fbbf24] flex items-center gap-1.5"><Award size={12}/> Coins earned</div>
                      <div className="text-3xl font-medium mono text-[#fbbf24] mt-1" data-testid="referral-coins">{ref.coins}</div>
                      <div className="text-[10px] text-white/50 mt-1">+{ref.coins_per_signup} per signup</div>
                    </div>
                    <div className="p-4 rounded-lg border border-[#a78bfa]/40 bg-white/3">
                      <div className="mono text-[10px] uppercase tracking-widest text-[#a78bfa] flex items-center gap-1.5"><Users size={12}/> Signups via you</div>
                      <div className="text-3xl font-medium mono text-[#a78bfa] mt-1" data-testid="referral-count">{ref.referred_count}</div>
                    </div>
                    <div className="p-4 rounded-lg border border-white/10 bg-white/3">
                      <div className="mono text-[10px] uppercase tracking-widest text-white/55 flex items-center gap-1.5"><LinkIcon size={12}/> Your code</div>
                      <div className="text-lg font-medium mono text-white mt-1" data-testid="referral-code">{ref.referral_code}</div>
                    </div>
                  </div>

                  {/* Share link */}
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-white/45 mb-1.5">// share your link</div>
                    <div className="flex items-stretch gap-2">
                      <div className="flex-1 flex items-center gap-2 px-3 rounded input" style={{ padding: "0 12px" }}>
                        <LinkIcon size={11} className="text-white/40 flex-shrink-0" />
                        <input readOnly value={ref.share_link} data-testid="referral-link" className="flex-1 bg-transparent outline-none text-[12px] py-2" onFocus={(e) => e.target.select()} />
                      </div>
                      <button data-testid="referral-copy" onClick={() => copy(ref.share_link)} className="btn-ghost text-xs px-3 flex items-center gap-1.5" style={{ border: "1px solid rgba(167,139,250,0.4)" }}>
                        {copied ? <Check size={11} className="text-emerald-300" /> : <Copy size={11} />}
                        {copied ? "Copied" : "Copy"}
                      </button>
                      <button data-testid="referral-share" onClick={shareNative} className="btn-ghost text-xs px-3 flex items-center gap-1.5" style={{ border: "1px solid rgba(59,130,246,0.4)" }}>
                        <Share2 size={11} /> Share
                      </button>
                    </div>
                  </div>

                  {/* Send email invite */}
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-white/45 mb-1.5 flex items-center gap-1.5">
                      <MailIcon size={11}/> // send an email invite
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-start">
                      <div className="space-y-2">
                        <input data-testid="referral-invite-email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="friend@company.com" className="input" />
                        <textarea data-testid="referral-invite-message" value={inviteMsg} onChange={(e) => setInviteMsg(e.target.value)} placeholder="(Optional) Personal message…" rows={2} className="input resize-y" />
                      </div>
                      <button
                        data-testid="referral-invite-send"
                        onClick={sendInviteEmail}
                        disabled={inviteBusy || !inviteEmail.trim()}
                        className="btn-accent text-xs px-4 py-2 flex items-center gap-1.5 h-fit"
                      >
                        {inviteBusy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                        Send invite
                      </button>
                    </div>
                    {inviteResult && (
                      <div className={`mt-2 text-[11px] mono ${inviteResult.sent ? "text-emerald-300" : inviteResult.queued ? "text-amber-300" : "text-red-300"}`}>
                        {inviteResult.sent ? "// invite sent" : inviteResult.queued ? `// ${inviteResult.reason}` : `// ${inviteResult.error || "failed"}`}
                      </div>
                    )}
                  </div>

                  {/* Boss/Referrer sees who signed up with them */}
                  {(ref.referred_users || []).length > 0 && (
                    <div>
                      <div className="mono text-[10px] uppercase tracking-widest text-white/45 mb-1.5">// signed up via your link ({ref.referred_users.length})</div>
                      <div className="rounded border border-white/10 bg-white/3 overflow-hidden">
                        {ref.referred_users.map((u) => (
                          <div key={u.id} className="px-3 py-2 flex items-center gap-3 border-b border-white/5 last:border-b-0 text-[12px]" data-testid={`referral-user-${(u.address||'').split('@')[0]}`}>
                            <div className="grid place-items-center w-7 h-7 rounded-full text-[11px] font-semibold text-white flex-shrink-0" style={{ background: "linear-gradient(135deg, #a78bfa, #3b82f6)" }}>
                              {(u.name || u.address || "?")[0].toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-white/90 truncate">{u.name}</div>
                              <div className="mono text-[10px] text-white/45 truncate">{u.address}</div>
                            </div>
                            <div className="mono text-[9px] uppercase tracking-widest text-[#a78bfa] px-1.5 py-0.5 rounded border border-[#a78bfa]/40 bg-[#a78bfa]/10">
                              {u.plan}
                            </div>
                            <div className="mono text-[10px] text-emerald-300">+{ref.coins_per_signup}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {tab === "notifications" && (
            <NotificationPreferences />
          )}

          {tab === "integrations" && (
            <div className="space-y-6">
              <ZeptomailIntegration user={user} />
              <ProviderSecretsPanel />
            </div>
          )}

          {tab === "reports" && (
            <ReportSubscriptionsPanel />
          )}

          {tab === "api" && (
            <ApiKeysPanel />
          )}
          {tab === "tracking" && isSuper && (
            <TrackingCodesPanel />
          )}
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.10);
          padding: 8px 10px;
          border-radius: 4px;
          font-size: 12.5px;
          color: rgba(255,255,255,0.95);
          outline: none;
        }
        .input:focus, .input:focus-within {
          border-color: rgba(167,139,250,0.5);
          background: rgba(255,255,255,0.06);
        }
        .btn-accent {
          background: rgba(167,139,250,0.15);
          color: #c7c1ff;
          border: 1px solid rgba(167,139,250,0.45);
          padding: 6px 12px;
          border-radius: 4px;
          transition: all 0.15s;
        }
        .btn-accent:hover:not(:disabled) { background: rgba(167,139,250,0.25); }
        .btn-accent:disabled { opacity: 0.4; }
      `}</style>
    </div>,
    document.body
  );
}

function Field({ label, children, disabled }) {
  return (
    <label className={`block ${disabled ? "opacity-70" : ""}`}>
      <div className="mono text-[9px] uppercase tracking-[0.28em] text-white/45 mb-1">{label}</div>
      {children}
    </label>
  );
}


function NotificationPreferences() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [testRes, setTestRes] = useState(null);

  const load = React.useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/me/notifications");
      setCfg(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const persist = async (next) => {
    setSaving(true); setErr(""); setMsg("");
    try {
      const r = await api.put("/me/notifications", next);
      setCfg(r.data);
      setMsg("Preferences saved");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleChannel = (key) => {
    const nextChannels = { ...(cfg?.channels || {}), [key]: !cfg?.channels?.[key] };
    setCfg({ ...cfg, channels: nextChannels });
    persist({ channels: nextChannels });
  };

  const toggleEnabled = () => {
    const next = !cfg?.enabled;
    setCfg({ ...cfg, enabled: next });
    persist({ enabled: next });
  };

  const sendTest = async () => {
    setTestBusy(true); setErr(""); setTestRes(null);
    try {
      const r = await api.post("/me/notifications/test");
      setTestRes(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setTestBusy(false);
    }
  };

  if (busy && !cfg) {
    return <div className="text-white/50 text-[12px] py-8 text-center flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading notification preferences…</div>;
  }
  if (!cfg) return null;

  const CATEGORIES = [
    { key: "new_lead",      label: "New lead captured",      hint: "When a fresh lead lands in the CRM inbox" },
    { key: "lead_assigned", label: "Lead assigned to me",    hint: "When a teammate assigns a lead your way" },
    { key: "deal_won",      label: "Deal won",               hint: "Celebrate every closed-won opportunity" },
    { key: "deal_lost",     label: "Deal lost",              hint: "Retrospectives — track why a deal slipped" },
    { key: "approvals",     label: "Approval requests",      hint: "Pending approvals waiting on you" },
    { key: "task_assigned", label: "Task assigned",          hint: "New Projects / To-do assignments" },
    { key: "invoice_paid",  label: "Invoice paid",           hint: "Accounts posts a paid invoice" },
    { key: "system_alerts", label: "System alerts",          hint: "Security, storage, health warnings" },
  ];

  const zeptoOk = cfg.delivery === "zeptomail";

  return (
    <div className="space-y-5" data-testid="profile-notifications">
      {/* Header + master toggle */}
      <div className="p-5 rounded-lg border border-white/10 bg-white/3">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md grid place-items-center"
               style={{ background: "linear-gradient(135deg,rgba(251,191,36,0.35),rgba(236,72,153,0.35))", border: "1px solid rgba(251,191,36,0.45)" }}>
            <Bell size={18} className="text-white" />
          </div>
          <div>
            <div className="text-white font-medium">Notification preferences</div>
            <div className="text-white/50 text-[11px]">
              Chosen events fire an email to <span className="mono text-[#a78bfa]">{cfg.admin_email}</span> via your Zeptomail sender.
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="mono text-[9px] uppercase tracking-[0.24em] text-white/60">
              {cfg.enabled ? "Enabled" : "Paused"}
            </span>
            <button
              data-testid="notif-master-toggle"
              onClick={toggleEnabled}
              disabled={saving}
              className="relative inline-flex items-center rounded-full transition"
              style={{
                width: 44, height: 22,
                background: cfg.enabled ? "rgba(34,197,94,0.5)" : "rgba(148,163,184,0.35)",
                border: `1px solid ${cfg.enabled ? "rgba(34,197,94,0.8)" : "rgba(148,163,184,0.55)"}`,
              }}
            >
              <span
                className="absolute rounded-full bg-white transition-all"
                style={{ width: 16, height: 16, top: 2, left: cfg.enabled ? 24 : 2 }}
              />
            </button>
          </div>
        </div>

        {!zeptoOk && (
          <div className="mb-3 px-3 py-2 rounded text-[11px]"
               style={{ background: "rgba(251,191,36,0.10)", color: "#fbbf24", border: "1px solid rgba(251,191,36,0.4)" }}>
            Zeptomail is not configured — configure it in the Integrations tab so notifications can be delivered.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {CATEGORIES.map((c) => {
            const on = !!cfg.channels?.[c.key];
            return (
              <button
                key={c.key}
                data-testid={`notif-channel-${c.key}`}
                onClick={() => toggleChannel(c.key)}
                disabled={saving || !cfg.enabled}
                className="flex items-start gap-3 p-3 rounded-lg text-left transition"
                style={{
                  background: on ? "rgba(139,92,246,0.10)" : "rgba(255,255,255,0.03)",
                  border: `1px solid ${on ? "rgba(139,92,246,0.45)" : "rgba(255,255,255,0.08)"}`,
                  opacity: cfg.enabled ? 1 : 0.55,
                }}
              >
                <div
                  className="mt-0.5 grid place-items-center rounded-full flex-shrink-0"
                  style={{
                    width: 16, height: 16,
                    background: on ? "#a78bfa" : "transparent",
                    border: on ? "1px solid #a78bfa" : "1px solid rgba(255,255,255,0.3)",
                  }}
                >
                  {on && <Check size={10} className="text-white" strokeWidth={3} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-white/95 text-[12.5px] font-medium">{c.label}</div>
                  <div className="text-white/50 text-[10.5px] mt-0.5">{c.hint}</div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-4 mt-4 border-t border-white/8">
          <button
            data-testid="notif-test-send"
            onClick={sendTest}
            disabled={testBusy || !zeptoOk}
            className="btn-accent text-xs flex items-center gap-1.5 px-4 py-2"
            title={zeptoOk ? "" : "Configure Zeptomail first"}
          >
            {testBusy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            Send test notification to admin
          </button>
          {saving && <span className="text-white/50 text-[11px] mono flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Saving…</span>}
          {msg && <span className="text-emerald-300 text-xs mono">{msg}</span>}
          {err && <span className="text-red-300 text-xs mono">{err}</span>}
        </div>

        {testRes && (
          <div
            data-testid="notif-test-result"
            className="mt-3 p-3 rounded text-[12px] mono"
            style={{
              background: testRes.sent ? "rgba(34,197,94,0.10)" : "rgba(244,63,94,0.10)",
              color:      testRes.sent ? "#86efac" : "#fda4af",
              border: `1px solid ${testRes.sent ? "rgba(34,197,94,0.4)" : "rgba(244,63,94,0.4)"}`,
            }}
          >
            {testRes.sent
              ? `✓ Test delivered to ${cfg.admin_email} (HTTP ${testRes.status_code || "OK"})`
              : `✗ Not sent — ${testRes.reason || testRes.status_code || "unknown"}`}
          </div>
        )}
      </div>

      <div className="p-4 rounded-lg border border-white/8 bg-white/3">
        <div className="mono text-[10px] uppercase tracking-[0.28em] text-white/50 mb-2">// How this works</div>
        <ul className="text-[12px] text-white/70 space-y-1.5 list-none">
          <li>• Whenever one of the events above fires, HEXA composes an email and sends it via <b className="text-white/90">your Zeptomail sender</b>.</li>
          <li>• The email is delivered to <b className="text-white/90">{cfg.admin_email}</b> so the super-admin has a live activity feed.</li>
          <li>• Uncheck any category to stop those emails; toggling the master switch off pauses everything.</li>
          <li>• Use the test button above to confirm the pipeline works end-to-end.</li>
        </ul>
      </div>
    </div>
  );
}


function ZeptomailIntegration({ user }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState("");
  const [fromAddr, setFromAddr] = useState("");
  const [fromName, setFromName] = useState("HEXA OS");
  const [region, setRegion] = useState("global");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [testTo, setTestTo] = useState(user?.address || "");
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/me/zeptomail");
      setCfg(r.data);
      setFromAddr(r.data.from_addr || "");
      setFromName(r.data.from_name || "HEXA OS");
      setRegion(r.data.region || "global");
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const payload = { from_addr: fromAddr.trim() || null, from_name: fromName.trim() || "HEXA OS", enabled: true, region };
      if (token.trim()) payload.token = token.trim();     // don't overwrite when field is left empty
      await api.post("/me/zeptomail", payload);
      setToken("");
      setMsg("Saved — Zeptomail is now handling your outgoing notifications.");
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Zeptomail? Notifications will queue until you reconfigure.")) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      await api.delete("/me/zeptomail");
      setToken(""); setFromAddr(""); setFromName("HEXA OS");
      setMsg("Zeptomail disconnected.");
      load();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    const to = (testTo || "").trim();
    if (!to.includes("@")) { setErr("Enter a valid recipient email."); return; }
    setTestBusy(true); setErr(""); setTestResult(null);
    try {
      const r = await api.post("/me/zeptomail/test", { to });
      setTestResult(r.data);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setTestBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="profile-integrations">
      <div className="p-5 rounded-lg border border-white/10 bg-white/3">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md grid place-items-center"
               style={{ background: "linear-gradient(135deg,rgba(59,130,246,0.35),rgba(167,139,250,0.35))", border: "1px solid rgba(167,139,250,0.45)" }}>
            <MailIcon size={18} className="text-[#c7c1ff]" />
          </div>
          <div>
            <div className="text-white font-medium">Zeptomail</div>
            <div className="text-white/50 text-[11px]">
              Route every HEXA notification (CRM · HR · Projects · Admin · Accounts · Invites) through your Zeptomail sender.
            </div>
          </div>
          <div className="ml-auto">
            {cfg?.configured ? (
              <span className="mono text-[9px] uppercase tracking-[0.24em] px-2 py-1 rounded"
                    data-testid="zepto-status"
                    style={{ background: "rgba(34,197,94,0.15)", color: "#86efac", border: "1px solid rgba(34,197,94,0.45)" }}>
                Connected
              </span>
            ) : (
              <span className="mono text-[9px] uppercase tracking-[0.24em] px-2 py-1 rounded"
                    data-testid="zepto-status"
                    style={{ background: "rgba(148,163,184,0.15)", color: "#94a3b8", border: "1px solid rgba(148,163,184,0.35)" }}>
                Not connected
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={cfg?.configured ? `API Token (current: ${cfg.token_masked || "•••"})` : "API Token"}>
            <input
              data-testid="zepto-token-input"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="input"
              placeholder={cfg?.configured ? "Paste a new key to replace it — leave blank to keep current" : "Paste your Zeptomail Send API Token here"}
            />
          </Field>
          <Field label="Sender Email (verified in Zeptomail)">
            <input
              data-testid="zepto-from-addr"
              type="email"
              value={fromAddr}
              onChange={(e) => setFromAddr(e.target.value)}
              className="input"
              placeholder="noreply@yourdomain.com"
            />
          </Field>
          <Field label="Sender Name">
            <input
              data-testid="zepto-from-name"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              className="input"
              placeholder="HEXA OS"
            />
          </Field>
          <Field label="Zeptomail Data Centre">
            <select
              data-testid="zepto-region"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="input"
            >
              {(cfg?.region_options || [{ value: "global", label: "Global (.com)" }]).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <div className="text-[11px] text-white/55 leading-relaxed pt-1.5">
              Must match the region your Zeptomail account was created in. A token from one
              data centre is rejected by the others as an invalid token.
            </div>
          </Field>
          <Field label="Where to get your token">
            <div className="text-[11px] text-white/60 leading-relaxed pt-1">
              Zeptomail Dashboard → <b>Mail Agents</b> → your agent → <b>Setup Info</b> → copy the <b>Send Mail API Token</b> value.
              We&apos;ll automatically prefix it with <code className="mono text-[10px] text-[#a78bfa]">Zoho-enczapikey</code>.
            </div>
          </Field>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-4 mt-4 border-t border-white/8">
          <button
            data-testid="zepto-save"
            onClick={save}
            disabled={busy || (!token.trim() && !cfg?.configured)}
            className="btn-accent text-xs flex items-center gap-1.5 px-4 py-2"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
            {cfg?.configured ? "Update" : "Save & Activate"}
          </button>
          {cfg?.configured && (
            <button
              data-testid="zepto-disconnect"
              onClick={disconnect}
              disabled={busy}
              className="text-xs flex items-center gap-1.5 px-3 py-2 rounded border"
              style={{ background: "rgba(244,63,94,0.10)", color: "#fda4af", borderColor: "rgba(244,63,94,0.4)" }}
            >
              <Trash2 size={11} /> Disconnect
            </button>
          )}
          {msg && <span className="text-emerald-300 text-xs mono">{msg}</span>}
          {err && <span className="text-red-300 text-xs mono">{err}</span>}
        </div>
      </div>

      {cfg?.configured && (
        <div className="p-5 rounded-lg border border-white/10 bg-white/3">
          <div className="text-white font-medium mb-3">Send a test email</div>
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Recipient">
              <input
                data-testid="zepto-test-to"
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                className="input"
                style={{ minWidth: 260 }}
              />
            </Field>
            <button
              data-testid="zepto-test-send"
              onClick={sendTest}
              disabled={testBusy}
              className="btn-accent text-xs flex items-center gap-1.5 px-4 py-2"
            >
              {testBusy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
              Send test
            </button>
          </div>
          {testResult && (
            <div
              data-testid="zepto-test-result"
              className="mt-3 p-3 rounded text-[12px] mono"
              style={{
                background: testResult.sent ? "rgba(34,197,94,0.10)" : "rgba(244,63,94,0.10)",
                color:      testResult.sent ? "#86efac" : "#fda4af",
                border: `1px solid ${testResult.sent ? "rgba(34,197,94,0.4)" : "rgba(244,63,94,0.4)"}`,
              }}
            >
              {testResult.sent
                ? `✓ Sent (HTTP ${testResult.status_code || "OK"}). Check ${testTo}.`
                : `✗ Not sent — reason: ${testResult.reason || testResult.status_code || "unknown"}.`}
            </div>
          )}
          {cfg?.last_test_ts && (
            <div className="text-[10px] text-white/40 mono mt-2">
              Last test: {new Date(cfg.last_test_ts).toLocaleString()} · status: {cfg.last_test_status}
            </div>
          )}
        </div>
      )}

      <div className="p-4 rounded-lg border border-white/8 bg-white/3">
        <div className="mono text-[10px] uppercase tracking-[0.28em] text-white/50 mb-2">// Where this token is used</div>
        <ul className="text-[12px] text-white/70 space-y-1.5 list-none">
          <li>• <b className="text-white/85">CRM</b> — new-lead assignment, follow-up reminders, deal-won notifications</li>
          <li>• <b className="text-white/85">Projects</b> — task assigned, deadline nearing, milestone hit</li>
          <li>• <b className="text-white/85">HR</b> — leave approvals, onboarding, offer letters</li>
          <li>• <b className="text-white/85">Admin</b> — password resets, invitations, security alerts</li>
          <li>• <b className="text-white/85">Accounts</b> — invoice sent, payment received, overdue reminder</li>
          <li>• <b className="text-white/85">Referrals & Marketing</b> — invite emails, campaign follow-ups</li>
        </ul>
      </div>
    </div>
  );
}


/* ─────────────────────────────  Integrations sub-panels  ───────────────────────────── */

function ProviderSecretsPanel() {
  const [secrets, setSecrets] = useState([]);
  const [providers, setProviders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [editing, setEditing] = useState(null);       // provider key or null

  const load = React.useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const [s, p] = await Promise.all([
        api.get("/admin/secrets"),
        api.get("/admin/secrets/providers"),
      ]);
      setSecrets(s.data || []);
      setProviders(p.data || []);
    } catch (e) {
      // Employees will get 403 — hide gracefully
      if (e?.response?.status === 403) setErr("");
      else setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const providerCards = [
    { key: "dialnexa_voice",    label: "DialNexa Voice",     tint: "#0ea5e9", accent: "sky",     desc: "Outbound calls & recordings from every CRM lead." },
    { key: "composio_whatsapp", label: "Composio WhatsApp",  tint: "#22c55e", accent: "green",   desc: "Send WhatsApp messages to leads via Composio." },
    { key: "twilio",            label: "Twilio (generic)",   tint: "#ef4444", accent: "red",     desc: "Fallback voice / SMS provider." },
    { key: "turn_server",       label: "TURN Server (WebRTC)", tint: "#8b5cf6", accent: "violet", desc: "Enables Meetings calls on mobile data & firewalls. Add URL, username and credential." },
  ];

  if (busy && !secrets.length && !providers.length) {
    return <div className="text-white/50 text-[12px] py-4 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading integrations…</div>;
  }
  if (!providers.length) return null; // employee — not authorized, silently hide

  return (
    <div className="space-y-3" data-testid="profile-provider-secrets">
      <div className="text-white/90 text-sm font-semibold">Provider integrations</div>
      <div className="text-white/45 text-[11px] mb-2">Add the vendor keys HEXA needs to place calls and send WhatsApp messages. Only admins and super-admins see this section.</div>
      {err && <div className="text-red-300 text-xs mono">{err}</div>}
      {msg && <div className="text-emerald-300 text-xs mono">{msg}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {providerCards.map((p) => {
          const existing = secrets.find(s => s.provider === p.key);
          return (
            <div key={p.key} className="rounded-lg p-4 border border-white/10 bg-white/3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded grid place-items-center" style={{ background: `${p.tint}22`, border: `1px solid ${p.tint}55` }}>
                  <Zap size={16} style={{ color: p.tint }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white/90 font-semibold text-[13px]">{p.label}</div>
                  <div className="text-white/50 text-[11px]">{p.desc}</div>
                </div>
                <span className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                  style={{
                    color: existing ? "#86efac" : "#94a3b8",
                    background: existing ? "rgba(34,197,94,0.10)" : "rgba(148,163,184,0.10)",
                    border: `1px solid ${existing ? "rgba(34,197,94,0.35)" : "rgba(148,163,184,0.30)"}`,
                  }}>
                  {existing ? "Connected" : "Not set"}
                </span>
              </div>
              {existing && (
                <div className="mt-2 space-y-0.5 text-[11px] mono">
                  {Object.entries(existing.masked || {}).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2 text-white/60 truncate">
                      <span className="text-white/40 lowercase">{k}</span>
                      <span className="text-white/85 truncate">{v || "—"}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 mt-3">
                <button
                  data-testid={`prov-secret-${p.key}-edit`}
                  onClick={() => setEditing({ provider: p.key, existing })}
                  className="text-[11px] px-3 py-1.5 rounded font-medium"
                  style={{ background: `linear-gradient(120deg,${p.tint},${p.tint}cc)`, color: "#fff" }}>
                  {existing ? "Rotate keys" : "Add keys"}
                </button>
                {existing && (
                  <button
                    data-testid={`prov-secret-${p.key}-delete`}
                    onClick={async () => {
                      if (!window.confirm(`Disconnect ${p.label}?`)) return;
                      try { await api.delete(`/admin/secrets/${existing.id}`); setMsg("Removed"); setTimeout(() => setMsg(""), 2000); load(); }
                      catch (e) { setErr(e?.response?.data?.detail || e.message); }
                    }}
                    className="text-[11px] px-2 py-1.5 rounded border border-rose-400/30 text-rose-300/80 hover:bg-rose-500/10">
                    <Trash2 size={11} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <InlineSecretEditor
          providerKey={editing.provider}
          existing={editing.existing}
          providers={providers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setMsg("Saved"); setTimeout(() => setMsg(""), 2000); load(); }}
        />
      )}
    </div>
  );
}

function InlineSecretEditor({ providerKey, existing, providers, onClose, onSaved }) {
  const cfg = providers.find(p => p.key === providerKey) || { fields: ["api_key"], label: providerKey };
  const [label, setLabel] = useState(existing?.label || cfg.label);
  const [values, setValues] = useState({});
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      if (existing) await api.put(`/admin/secrets/${existing.id}`, { provider: providerKey, label, values });
      else          await api.post("/admin/secrets", { provider: providerKey, label, values });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[400] grid place-items-center p-4" style={{ background: "rgba(4,7,20,0.7)" }} onClick={onClose}>
      <div className="rounded-xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}
        style={{ background: "#0c1224", border: "1px solid rgba(139,92,246,0.35)", boxShadow: "0 24px 60px rgba(0,0,0,0.5)" }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded grid place-items-center" style={{ background: "linear-gradient(135deg,#a855f7,#ec4899)" }}>
            <KeyRound size={14} className="text-white" />
          </div>
          <div className="flex-1">
            <div className="text-white/90 font-medium">{existing ? "Rotate" : "Add"} {cfg.label}</div>
            <div className="text-white/45 text-[11px]">Values are sealed at rest and only shown masked.</div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white/80"><X size={14} /></button>
        </div>
        <label className="block mb-2">
          <div className="mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Label</div>
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} />
        </label>
        <div className="flex items-center justify-between mb-1">
          <div className="mono text-[9px] uppercase tracking-widest text-white/50">Keys</div>
          <button onClick={() => setReveal(v => !v)} className="text-[11px] text-white/50 hover:text-white/85">
            {reveal ? "Hide" : "Show"}
          </button>
        </div>
        <div className="space-y-1.5">
          {(cfg.fields || []).map((k) => (
            <label key={k} className="block">
              <div className="text-[10px] text-white/50 mono lowercase mb-0.5">{k}</div>
              <input type={reveal ? "text" : "password"} className="input"
                value={values[k] || ""}
                onChange={(e) => setValues({ ...values, [k]: e.target.value })}
                placeholder={existing ? "•••••• (leave empty to keep current)" : "paste key…"}
                data-testid={`prov-secret-field-${k}`}
              />
            </label>
          ))}
        </div>
        {err && <div className="text-red-300 text-xs mono mt-2">{err}</div>}
        <div className="flex items-center justify-end gap-2 pt-3 mt-3 border-t border-white/8">
          <button onClick={onClose} className="text-white/60 text-[11px] px-3 py-2">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="prov-secret-save"
            className="btn-accent text-xs flex items-center gap-1.5 px-4 py-2">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

function ApiKeysPanel() {
  const [widgets, setWidgets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState("");
  const base = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    (async () => {
      setBusy(true); setErr("");
      try { const r = await api.get("/admin/widgets"); setWidgets(r.data || []); }
      catch (e) {
        if (e?.response?.status === 403) setErr("");
        else setErr(e?.response?.data?.detail || e.message);
      }
      finally { setBusy(false); }
    })();
  }, []);

  const forms = widgets.filter(w => w.kind === "form");
  const assistants = widgets.filter(w => w.kind === "assistant");

  const copy = async (text, key) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(""), 1500); }
    catch (_) { window.prompt("Copy this:", text); }
  };

  const section = (title, list, Icon, tint) => (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded grid place-items-center" style={{ background: `${tint}22`, border: `1px solid ${tint}55` }}>
          <Icon size={15} style={{ color: tint }} />
        </div>
        <div>
          <div className="text-white/90 font-semibold text-[13px]">{title}</div>
          <div className="text-white/45 text-[11px]">{list.length} configured</div>
        </div>
      </div>
      {list.length === 0 ? (
        <div className="rounded-md border border-dashed border-white/10 bg-white/3 p-4 text-center text-white/45 text-[11.5px]">
          No {title.toLowerCase()} yet. Create one in <b className="text-white/70">Admin AI → Integrations</b>.
        </div>
      ) : (
        <div className="space-y-1.5">
          {list.map((w) => {
            const snippet = w.kind === "form"
              ? `<div id="hexa-widget-${w.id}"></div>\n<script src="${base}/api/public/widget/${w.id}.js" async></script>`
              : `<script src="${base}/api/public/widget/${w.id}.js" async></script>`;
            return (
              <div key={w.id} className="rounded-md p-3 border border-white/8 bg-white/3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="mono text-[10px] uppercase tracking-widest text-white/55">API Key</span>
                  <span className="text-white/85 text-[12.5px] flex-1 truncate">{w.name}</span>
                  <span className="mono text-[9px] px-2 py-0.5 rounded-full uppercase tracking-widest"
                    style={{ color: w.active ? "#86efac" : "#94a3b8", background: w.active ? "rgba(34,197,94,0.10)" : "rgba(148,163,184,0.10)", border: `1px solid ${w.active ? "rgba(34,197,94,0.35)" : "rgba(148,163,184,0.30)"}` }}>
                    {w.active ? "Live" : "Paused"}
                  </span>
                </div>
                <div className="mono text-[10.5px] text-white/60 bg-black/30 rounded px-2 py-1.5 truncate">{w.id}</div>
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  <button
                    onClick={() => copy(w.id, `id-${w.id}`)}
                    data-testid={`api-copy-id-${w.id}`}
                    className="text-[10.5px] px-2.5 py-1 rounded border border-white/10 text-white/70 hover:bg-white/8 flex items-center gap-1">
                    {copied === `id-${w.id}` ? <Check size={10} /> : <Copy size={10} />} Copy key
                  </button>
                  <button
                    onClick={() => copy(snippet, `code-${w.id}`)}
                    data-testid={`api-copy-code-${w.id}`}
                    className="text-[10.5px] px-2.5 py-1 rounded font-medium text-white flex items-center gap-1"
                    style={{ background: `linear-gradient(120deg,${tint},${tint}cc)` }}>
                    {copied === `code-${w.id}` ? <Check size={10} /> : <Code size={10} />} Copy embed code
                  </button>
                  <a href={`${base}/api/public/widget/${w.id}.js`} target="_blank" rel="noreferrer"
                    className="text-[10.5px] px-2.5 py-1 rounded border border-white/10 text-white/70 hover:bg-white/8 flex items-center gap-1 ml-auto">
                    <ExternalLink size={10} /> Preview
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  if (busy) return <div className="text-white/50 text-[12px] py-4 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading API keys…</div>;

  return (
    <div className="space-y-6" data-testid="profile-api-panel">
      <div>
        <div className="text-white/90 text-sm font-semibold">HEXA API keys</div>
        <div className="text-white/45 text-[11px]">Embed HEXA Forms and AI Assistants anywhere. Every widget id below is a public embed key — visitors who submit or chat via that key show up instantly in your CRM.</div>
      </div>
      {err && <div className="text-red-300 text-xs mono">{err}</div>}
      {section("HEXA Forms", forms, FileText, "#0ea5e9")}
      {section("HEXA AI Assistants", assistants, MessageCircle, "#a855f7")}
    </div>
  );
}


// ============================================================
// TRACKING CODES PANEL (Super Admin only)
// ============================================================
function TrackingCodesPanel() {
  const [codes, setCodes] = useState({ ga4_id: "", gtm_id: "", pixel_id: "", ga_enabled: false, pixel_enabled: false, gtm_enabled: false });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/tracking-codes");
        setCodes(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put("/admin/tracking-codes", codes);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-white/50 text-sm"><Loader2 size={14} className="animate-spin" /> Loading...</div>;

  return (
    <div className="space-y-6" data-testid="tracking-codes-panel">
      <div>
        <div className="flex items-center gap-2 text-white font-semibold text-sm">
          <Radio size={14} className="text-cyan-400" />
          Website Tracking Codes
        </div>
        <div className="text-white/45 text-[11px] mt-0.5">Connect Google Analytics and Facebook Pixel to track visitor behavior on hexawebos.com. These codes are injected automatically into the public site.</div>
      </div>

      {/* Google Analytics 4 */}
      <div className="rounded-xl p-4 space-y-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-orange-500/20 flex items-center justify-center">
              <span className="text-orange-400 text-[10px] font-bold">GA</span>
            </div>
            <span className="text-white text-sm font-semibold">Google Analytics 4</span>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-xs text-white/50">{codes.ga_enabled ? "Enabled" : "Disabled"}</span>
            <div
              onClick={() => setCodes(c => ({ ...c, ga_enabled: !c.ga_enabled }))}
              className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative ${codes.ga_enabled ? "bg-orange-500" : "bg-white/10"}`}
            >
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all shadow ${codes.ga_enabled ? "left-5" : "left-0.5"}`} />
            </div>
          </label>
        </div>
        <div>
          <label className="text-[11px] text-white/50 block mb-1">Measurement ID</label>
          <input
            value={codes.ga4_id}
            onChange={e => setCodes(c => ({ ...c, ga4_id: e.target.value }))}
            placeholder="G-XXXXXXXXXX"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-orange-400 transition-colors font-mono"
          />
          <p className="text-[10px] text-white/30 mt-1">Find this in Google Analytics → Admin → Data Streams → your stream → Measurement ID</p>
        </div>
      </div>

      {/* Google Tag Manager */}
      <div className="rounded-xl p-4 space-y-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <span className="text-blue-400 text-[10px] font-bold">GTM</span>
            </div>
            <span className="text-white text-sm font-semibold">Google Tag Manager</span>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-xs text-white/50">{codes.gtm_enabled ? "Enabled" : "Disabled"}</span>
            <div
              onClick={() => setCodes(c => ({ ...c, gtm_enabled: !c.gtm_enabled }))}
              className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative ${codes.gtm_enabled ? "bg-blue-500" : "bg-white/10"}`}
            >
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all shadow ${codes.gtm_enabled ? "left-5" : "left-0.5"}`} />
            </div>
          </label>
        </div>
        <div>
          <label className="text-[11px] text-white/50 block mb-1">Container ID</label>
          <input
            value={codes.gtm_id}
            onChange={e => setCodes(c => ({ ...c, gtm_id: e.target.value }))}
            placeholder="GTM-XXXXXXX"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-blue-400 transition-colors font-mono"
          />
        </div>
      </div>

      {/* Facebook Pixel */}
      <div className="rounded-xl p-4 space-y-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-blue-600/20 flex items-center justify-center">
              <span className="text-blue-300 text-[10px] font-bold">f</span>
            </div>
            <span className="text-white text-sm font-semibold">Meta (Facebook) Pixel</span>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-xs text-white/50">{codes.pixel_enabled ? "Enabled" : "Disabled"}</span>
            <div
              onClick={() => setCodes(c => ({ ...c, pixel_enabled: !c.pixel_enabled }))}
              className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative ${codes.pixel_enabled ? "bg-blue-600" : "bg-white/10"}`}
            >
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all shadow ${codes.pixel_enabled ? "left-5" : "left-0.5"}`} />
            </div>
          </label>
        </div>
        <div>
          <label className="text-[11px] text-white/50 block mb-1">Pixel ID</label>
          <input
            value={codes.pixel_id}
            onChange={e => setCodes(c => ({ ...c, pixel_id: e.target.value }))}
            placeholder="123456789012345"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-blue-400 transition-colors font-mono"
          />
          <p className="text-[10px] text-white/30 mt-1">Find this in Meta Business Manager → Events Manager → your pixel</p>
        </div>
      </div>

      <button
        data-testid="save-tracking-codes"
        onClick={save}
        disabled={saving}
        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all"
        style={{ background: "linear-gradient(120deg,#f97316,#06b6d4)", boxShadow: "0 4px 12px rgba(249,115,22,0.2)" }}
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <Check size={14} /> : <Save size={14} />}
        {saved ? "Saved!" : "Save Tracking Codes"}
      </button>

      {/* Info */}
      <div className="rounded-xl p-4" style={{ background: "rgba(6,182,212,0.05)", border: "1px solid rgba(6,182,212,0.2)" }}>
        <p className="text-cyan-300/70 text-xs leading-relaxed">
          <strong className="text-cyan-300">How it works:</strong> These tracking codes are fetched by the HEXA OS frontend on load and injected into the &lt;head&gt; of the page dynamically. Toggle "Enabled" to activate/deactivate without losing your IDs.
        </p>
      </div>
    </div>
  );
}



// ============ REPORT SUBSCRIPTIONS PANEL (Part E) ============
const FREQUENCIES = [
  { key: "daily",       label: "Daily",           desc: "Every morning" },
  { key: "weekly",      label: "Weekly",          desc: "Every Monday" },
  { key: "monthly",     label: "Monthly",         desc: "1st of each month" },
  { key: "q1",          label: "Q1 (Apr–Jun)",    desc: "Sent 1 July" },
  { key: "q2",          label: "Q2 (Jul–Sep)",    desc: "Sent 1 October" },
  { key: "q3",          label: "Q3 (Oct–Dec)",    desc: "Sent 1 January" },
  { key: "q4",          label: "Q4 (Jan–Mar)",    desc: "Sent 1 April" },
  { key: "half_yearly", label: "Half Yearly",     desc: "Apr–Sep, sent 1 Oct" },
  { key: "yearly",      label: "Yearly (Apr–Mar)","desc": "Sent 1 April" },
];

const MODULES = [
  { key: "crm",        label: "CRM / Sales",          color: "#8b5cf6" },
  { key: "attendance", label: "Attendance & Workforce",color: "#f59e0b" },
  { key: "tasks",      label: "Tasks Summary",         color: "#3b82f6" },
  { key: "finance",    label: "Accounts & Finance",    color: "#22c55e" },
  { key: "blog",       label: "Blog & Website Stats",  color: "#ec4899" },
];

function ReportSubscriptionsPanel() {
  const [subs, setSubs] = useState({ frequencies: [], modules: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/me/report-subscriptions")
      .then(r => { setSubs(r.data || { frequencies: [], modules: [] }); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = (list, key) => {
    const arr = subs[list] || [];
    return arr.includes(key) ? arr.filter(k => k !== key) : [...arr, key];
  };

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await api.put("/me/report-subscriptions", { frequencies: subs.frequencies, modules: subs.modules });
      setMsg("Report preferences saved!");
    } catch (e) {
      setMsg("Error: " + (e?.response?.data?.detail || "unknown"));
    }
    setSaving(false);
  };

  if (loading) return <div className="text-white/50 text-xs py-4 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-5" data-testid="report-subscriptions-panel">
      <div>
        <div className="text-white/90 text-sm font-semibold mb-0.5">Scheduled Report Emails</div>
        <div className="text-white/45 text-[11px]">Choose which reports to receive and how often. Reports use your workspace's Zeptomail key.</div>
      </div>

      {/* Frequency selector */}
      <div>
        <div className="text-white/65 text-xs font-medium mb-2 mono uppercase tracking-widest">Frequency</div>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {FREQUENCIES.map(f => {
            const active = (subs.frequencies || []).includes(f.key);
            return (
              <button
                key={f.key}
                data-testid={`freq-${f.key}`}
                onClick={() => setSubs(s => ({ ...s, frequencies: toggle("frequencies", f.key) }))}
                className="p-2 rounded-lg border text-left transition-all"
                style={{
                  background: active ? "rgba(139,92,246,0.15)" : "rgba(255,255,255,0.04)",
                  borderColor: active ? "rgba(139,92,246,0.6)" : "rgba(255,255,255,0.10)",
                }}
              >
                <div className={`text-[11px] font-semibold ${active ? "text-violet-300" : "text-white/70"}`}>{f.label}</div>
                <div className="text-[9px] text-white/35 mt-0.5">{f.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Module selector */}
      <div>
        <div className="text-white/65 text-xs font-medium mb-2 mono uppercase tracking-widest">Report Modules</div>
        <div className="text-white/40 text-[10px] mb-2">Leave all unchecked to receive all modules your role allows.</div>
        <div className="space-y-2">
          {MODULES.map(m => {
            const active = (subs.modules || []).includes(m.key);
            return (
              <label
                key={m.key}
                data-testid={`module-${m.key}`}
                className="flex items-center gap-3 cursor-pointer group"
              >
                <div
                  className="w-4 h-4 rounded flex items-center justify-center border transition-all"
                  style={{
                    background: active ? m.color : "transparent",
                    borderColor: active ? m.color : "rgba(255,255,255,0.25)",
                  }}
                  onClick={() => setSubs(s => ({ ...s, modules: toggle("modules", m.key) }))}
                >
                  {active && <Check size={10} color="#fff" />}
                </div>
                <span className={`text-[12px] ${active ? "text-white/90" : "text-white/50"}`}>{m.label}</span>
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: m.color }} />
              </label>
            );
          })}
        </div>
      </div>

      {msg && <div className={`text-xs mono ${msg.startsWith("Error") ? "text-red-300" : "text-emerald-300"}`}>{msg}</div>}
      <button
        data-testid="save-report-subs"
        onClick={save}
        disabled={saving}
        className="flex items-center gap-2 px-4 py-2 rounded text-[11px] font-semibold text-white mono uppercase tracking-widest transition"
        style={{ background: "linear-gradient(90deg,#8b5cf6,#3b82f6)" }}
      >
        {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
        Save Preferences
      </button>
    </div>
  );
}
